import React from 'react';
import { Globe } from 'lucide-react';
import { REGION_CODES, REGION_NAME, REGION_CC } from '../../utils/regions';

const FLAG_BASE = 'https://flagcdn.com/w40';
const FLAG_BASE_2X = 'https://flagcdn.com/w80';

const REGIONS = REGION_CODES.map((code) => ({
  code,
  label: REGION_NAME[code],
  cc: REGION_CC[code] || null,
}));

const FlagIcon = ({ cc, alt }) => {
  if (!cc) {
    return <Globe size={18} className="flag-svg" aria-hidden="true" />;
  }
  return (
    <img
      src={`${FLAG_BASE}/${cc}.png`}
      srcSet={`${FLAG_BASE}/${cc}.png 1x, ${FLAG_BASE_2X}/${cc}.png 2x`}
      alt={alt}
      width="22"
      height="14"
      className="flag-img"
      loading="lazy"
    />
  );
};

const RegionSelector = ({ active, onChange }) => {
  return (
    <div className="region-selector" data-testid="region-selector">
      <div className="region-inner">
        <span className="region-eyebrow">Browse by region</span>
        <div className="region-pills">
          {REGIONS.map((r) => {
            const isActive = active === r.code;
            return (
              <button
                key={r.code}
                type="button"
                data-testid={`region-pill-${r.code}`}
                className={`region-pill ${isActive ? 'is-active' : ''}`}
                onClick={() => onChange(r.code)}
                aria-pressed={isActive}
                aria-label={r.label}
                title={r.label}
              >
                <FlagIcon cc={r.cc} alt={r.label + ' flag'} />
                <span className="region-pill-label">{r.label}</span>
              </button>
            );
          })}
        </div>
      </div>

      <style jsx>{`
        .region-selector {
          position: sticky;
          top: 0;
          z-index: 40;
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          background: rgba(10, 10, 10, 0.85);
          border-bottom: 1px solid #1f1f1f;
          padding: 1rem 0;
        }
        .region-inner {
          max-width: 1280px;
          margin: 0 auto;
          padding: 0 1.5rem;
          display: flex;
          align-items: center;
          gap: 1.25rem;
          flex-wrap: wrap;
        }
        .region-eyebrow {
          color: #6f6f78;
          font-size: 0.7rem;
          font-weight: 700;
          letter-spacing: 0.18em;
          text-transform: uppercase;
        }
        .region-pills {
          display: flex;
          gap: 0.5rem;
          flex-wrap: wrap;
        }
        .region-pill {
          background: #161616;
          color: #fff;
          border: 1px solid #262626;
          padding: 0.5rem 0.95rem;
          border-radius: 999px;
          font-size: 0.85rem;
          font-weight: 600;
          cursor: pointer;
          transition: background 0.18s, border-color 0.18s, transform 0.18s,
            box-shadow 0.18s;
          display: inline-flex;
          align-items: center;
          gap: 0.55rem;
          line-height: 1;
        }
        :global(.flag-img) {
          display: inline-block;
          border-radius: 2px;
          box-shadow: 0 0 0 1px rgba(255, 255, 255, 0.06);
          vertical-align: middle;
        }
        :global(.flag-svg) {
          color: currentColor;
        }
        .region-pill:hover {
          background: #1f1f1f;
          border-color: #3a3a3a;
          transform: translateY(-1px);
        }
        .region-pill.is-active {
          background: #E62F88;
          color: #fff;
          border-color: #E62F88;
          box-shadow: 0 0 18px rgba(230, 47, 136, 0.4);
        }
        .region-pill-label {
          font-weight: 600;
        }
        @media (max-width: 540px) {
          /* Compact: hide the long country names on the tightest screens but keep flag + active label */
          .region-pill .region-pill-label {
            display: none;
          }
          .region-pill.is-active .region-pill-label {
            display: inline;
          }
        }
      `}</style>
    </div>
  );
};

export default RegionSelector;
